"use client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import Slider from "react-slick";

import Brands from "shared-lib/Brands";
import { ClickServer } from "shared-lib/clickserver";
import * as gtag from "shared-lib/gtag";
import { brand } from "shared-lib/helpers/envFunctions";
import {
  TRACKING_EVENTS,
  logStatsig,
  TRACKING_CATEGORIES,
} from "shared-lib/helpers/statsigFunctions";
import { getImageDimensions } from "shared-lib/helpers/utilityFunctions";

import RecipeImage from "@/components/recipe/image";
import { getRecipeImageURLs } from "@/lib/helpers/backendFunctions";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function arrowClick(pageTitle: string, direction: string) {
  gtag.event({
    action: TRACKING_EVENTS.image_carousel_nav,
    category: TRACKING_CATEGORIES.interaction,
    label: pageTitle,
  });
  logStatsig(TRACKING_EVENTS.image_carousel_nav, { direction });
  ClickServer.track(TRACKING_EVENTS.image_carousel_nav, { direction });
}

const commonClassNames =
  "absolute top-1/2 z-10 h-7 w-7 cursor-pointer rounded-full bg-white/75 transition-colors duration-200 hover:stroke-blue-400";

function NextArrow({
  onClick = () => {
    return;
  },
  pageTitle = "",
}) {
  return (
    <ChevronRightIcon
      id="nextArrowCarouselSingle"
      className={`!right-0 mr-1 ${commonClassNames}`}
      onClick={() => {
        onClick();
        arrowClick(pageTitle, "right");
      }}
    />
  );
}

function PrevArrow({
  onClick = () => {
    return;
  },
  pageTitle = "",
}) {
  return (
    <ChevronLeftIcon
      id="prevArrowCarouselSingle"
      className={`!left-0 ml-1 ${commonClassNames}`}
      onClick={() => {
        onClick();
        arrowClick(pageTitle, "left");
      }}
    />
  );
}

const indexMap = new Map([
  [Brands.commandcooking, 0],
  [Brands.heartlandcooking, 1],
  [Brands.cookingprofessionally, 2],
  [Brands.mamascooking, 3],
  [Brands.recipesaver, 4],
  [Brands.lazycooking, 5],
  [Brands.easiereats, 6],
  [Brands.recipereader, 7],
  [Brands.recipesdirect, 8],
]);

const brandSlideIdx = indexMap.get(brand.id)!;

export default function RecipeCarouselSingle({
  className = "",
  recipe = {} as Recipe,
  size = "medium",
}) {
  const [width, height] = getImageDimensions(size);
  const urls = getRecipeImageURLs(recipe, width, height);
  const initialSlide = brandSlideIdx <= urls.length ? brandSlideIdx : 0;

  const settings = {
    autoplay: false,
    autoplaySpeed: 3000,
    infinite: true,
    initialSlide,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    dots: true,
    speed: 750,
    nextArrow: <NextArrow pageTitle={recipe.title} />,
    prevArrow: <PrevArrow pageTitle={recipe.title} />,
    onSwipe: (swipeDirection: string) => {
      // Swiping to the left causes a Next event, which is right
      const carouselDirection = swipeDirection === "right" ? "left" : "right";
      arrowClick(recipe.title ?? "", carouselDirection);
    },
  };

  return urls.length > 1 ? (
    // @ts-expect-error Suddenly started complaining
    <Slider
      key={initialSlide}
      {...settings}
      className={clsx(className, "carousel-slider")}
    >
      {urls.map((url, index: number) => (
        <div key={`carousel-image-${index}`}>
          <RecipeImage
            priority
            recipe={recipe}
            urlSrc={url}
            size={"medium"}
            className="mx-auto my-2 h-64"
          />
        </div>
      ))}
    </Slider>
  ) : (
    <RecipeImage
      priority
      extraRound
      recipe={recipe}
      size="large"
      className="mt-4 h-auto w-full md:w-96"
    />
  );
}
